// SL.L1a-2 (#2957, ruling 11; recon Q6, Q7) — one sprint's actions, in its
// header row. Each button is rendered if and only if the server lists its
// action in this sprint's allowedActions (W1): no role or state logic here.
// With several active sprints each row carries its own bar, so every request
// and dialog names its own sprintId.
//
// Approve, Return to draft, Start and plain Close ask to confirm first. Close
// with override opens its dialog. After a success the board re-reads the
// sprints (and the issues, after a close) and announces it; after a refusal it
// shows the server's text (#3055) and re-reads, so the buttons match the
// server. While a request is in flight this sprint's buttons are disabled.
import { useState, type CSSProperties } from 'react';
import { hasAction, type SprintAction } from '../../lib/sprintLoopActions';
import type { Sprint } from '../../lib/sprintsApi';
import { postSprintTransition, refusalText, type SprintTransitionPath } from '../../lib/sprintActionsApi';
import SprintConfirmDialog, { dialogButton } from './SprintConfirmDialog';
import CloseOverrideDialog from './CloseOverrideDialog';

export interface BoardNotice { text: string; error: boolean }

export interface SprintActionBarProps {
  projectId: string;
  sprint: Sprint;
  /** Re-read GET /sprints, and the issues too when `issues`. Never rejects. */
  reload: (opts: { issues: boolean }) => Promise<void>;
  onNotice: (n: BoardNotice) => void;
}

interface Transition {
  action: SprintAction;
  path: SprintTransitionPath;
  button: string;
  label: (s: Sprint) => string;
  title: (s: Sprint) => string;
  message: string;
  confirm: string;
  done: (s: Sprint) => string;
}

const TRANSITIONS: readonly Transition[] = [
  {
    action: 'approve', path: 'approve', button: 'Approve', label: (s) => `Approve ${s.name}`, title: (s) => `Approve ${s.name}?`,
    message: 'Approving fixes the sprint plan and records its planned points.', confirm: 'Approve', done: (s) => `${s.name} approved`,
  },
  {
    action: 'return_to_draft', path: 'return-to-draft', button: 'Return to draft', label: (s) => `Return ${s.name} to draft`,
    title: (s) => `Return ${s.name} to draft?`, message: 'The sprint can then be changed and approved again.',
    confirm: 'Return to draft', done: (s) => `${s.name} returned to draft`,
  },
  {
    action: 'start', path: 'start', button: 'Start', label: (s) => `Start ${s.name}`, title: (s) => `Start ${s.name}?`,
    message: 'Starting makes this the active sprint.', confirm: 'Start', done: (s) => `${s.name} started`,
  },
  {
    action: 'close', path: 'close', button: 'Close', label: (s) => `Close ${s.name}`, title: (s) => `Close ${s.name}?`,
    message: 'Every item is done or cancelled. Closing records the sprint\'s velocity.', confirm: 'Close sprint',
    done: (s) => `Sprint ${s.number} closed`,
  },
];

const barButton: CSSProperties = { ...dialogButton, padding: '4px 12px', fontSize: 12 };

export default function SprintActionBar({ projectId, sprint, reload, onNotice }: SprintActionBarProps) {
  const [confirming, setConfirming] = useState<Transition | null>(null);
  const [overriding, setOverriding] = useState(false);
  const [busy, setBusy] = useState(false);

  const listed = TRANSITIONS.filter((t) => hasAction(sprint.allowedActions, t.action));
  const canOverride = hasAction(sprint.allowedActions, 'close_with_override');
  if (listed.length === 0 && !canOverride) return null;

  async function run(t: Transition) {
    setBusy(true);
    try {
      await postSprintTransition(projectId, sprint.id, t.path);
      setConfirming(null);
      await reload({ issues: t.action === 'close' });
      onNotice({ text: t.done(sprint), error: false });
    } catch (e) {
      setConfirming(null);
      onNotice({ text: refusalText(e, "The sprint couldn't be updated."), error: true });
      await reload({ issues: false });
    } finally {
      setBusy(false);
    }
  }

  // The dialogs sit beside the group, not in it: the group holds only this sprint's buttons.
  return (
    <>
      <span role="group" aria-label={`${sprint.name} actions`} style={{ display: 'inline-flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
        {listed.map((t) => (
          <button key={t.action} type="button" aria-label={t.label(sprint)} disabled={busy} onClick={() => setConfirming(t)} style={barButton}>
            {t.button}
          </button>
        ))}
        {canOverride && (
          <button type="button" aria-label={`Close ${sprint.name} with override`} disabled={busy} onClick={() => setOverriding(true)} style={barButton}>
            Close with override
          </button>
        )}
      </span>
      {confirming && (
        <SprintConfirmDialog
          title={confirming.title(sprint)} message={confirming.message} confirmLabel={confirming.confirm} busy={busy}
          onConfirm={() => void run(confirming)} onCancel={() => setConfirming(null)}
        />
      )}
      {overriding && (
        <CloseOverrideDialog
          projectId={projectId} sprint={sprint} onBusy={setBusy}
          onClosed={() => {
            setOverriding(false);
            void reload({ issues: true }).then(() => onNotice({ text: `Sprint ${sprint.number} closed`, error: false }));
          }}
          onRefused={() => reload({ issues: false })}
          onDismiss={(notice) => {
            setOverriding(false);
            if (notice) onNotice({ text: notice, error: true });
          }}
        />
      )}
    </>
  );
}
